/**
 * Identity of a patch operation - what it targets, as opposed to what it does to it.
 *
 * Used to decide when a direct, store-level edit conflicts with an all-store operation. Two
 * operations sharing a conflict key are competing for the same menu node, so for any store that has
 * a direct edit, the all-store operation is skipped and the store's own choice is kept.
 */

/**
 * The keys `mapPatchOperations` understands, in the order they are probed. Minimal operations are
 * single-key objects (`{ setVariantPrice: {...} }`), but they may also carry `__typename`, so the
 * operation key is found by lookup rather than by taking the object's only key.
 */
const OPERATION_KEYS = [
  'setVariantPrice',
  'setBundlePrice',
  'setNamedPriceAmount',
  'setModifierPriceRanges',
  'setModifierWeightPrice',
  'setModifierWeightIsExcludedFromVariant',
  'removeProduct',
  'removeVariant',
  'removeModifier',
  'removeModifierFromSlot',
  'removeWeightFromSlotModifier',
  'removeBundle',
] as const;

export type OperationKey = typeof OPERATION_KEYS[number];

/**
 * The operation key on a minimal operation object, or null when it is not one we can identify.
 * Tolerates `__typename` and stray fields - a missed key would silently disable conflict detection,
 * so this is deliberately more forgiving than `mapPatchOperations`' exact single-key check.
 */
export const getOperationKey = (operation: any): OperationKey | null => {
  if (!operation || typeof operation !== 'object') return null;
  return OPERATION_KEYS.find((key) => operation[key] != null) ?? null;
};

/**
 * Ids may sit nested under the entity (`variant.variantId`, straight off the payload) or flat
 * (`variantId`, as the form builds them). Same fallback order `mapPatchOperations` uses.
 */
const id = (data: any, entity: string, field: string): string =>
  data?.[entity]?.[field] ?? data?.[field] ?? '';

/**
 * A modifier weight's id, wherever it happens to live.
 *
 * The form builds `weightId`, but a `getPatch` node nests a `ModifierWeightDefinition` whose key field
 * is `modifierWeightId`, and the entity itself may be under `weight` or `modifierWeight`. All four
 * spellings must land on one id or a node-derived operation will not match a form-derived one.
 */
export const readWeightId = (data: any): string =>
  data?.weight?.modifierWeightId ??
  data?.weight?.weightId ??
  data?.modifierWeight?.modifierWeightId ??
  data?.modifierWeight?.weightId ??
  data?.weightId ??
  '';

/**
 * Stable identity of what an operation targets.
 *
 * The key deliberately ignores whether the operation is a set or a remove: a store-level *delete* of
 * a named price is meant to suppress an all-store *set* of that same named price, so both must land
 * on one key. For the same reason `setVariantPrice` and `removeVariant` share `variant:<id>`, and
 * excluding a modifier weight shares a key with pricing it.
 *
 * Known limitation, currently unreachable: `removeWeightFromSlotModifier` is slot-scoped where
 * `setModifierWeightPrice` is variant-scoped, so they get separate keys and do not suppress one
 * another. The two live in different patch types, and the bulk update page clears pending operations
 * before allowing a patch type change, so they cannot both be pending. Worth revisiting only if that
 * guard is relaxed.
 *
 * Returns `unknown:<key|no-key>` for operations we cannot identify, so they group deterministically
 * rather than colliding with a real target.
 */
export const getOperationConflictKey = (operation: any): string => {
  // Accepts both shapes: a minimal `{ setVariantPrice: {...} }` and a `getPatch` node carrying its
  // fields directly under a `__typename`.
  const normalized = normalizeOperationNode(operation);
  if (!normalized) return 'unknown:no-key';

  const { key, data } = normalized;

  switch (key) {
    case 'setNamedPriceAmount':
      return `namedPrice:${id(data, 'namedPrice', 'namedPriceId')}`;

    case 'setVariantPrice':
    case 'removeVariant':
      return `variant:${id(data, 'variant', 'variantId')}`;

    case 'setBundlePrice':
    case 'removeBundle':
      return `bundle:${id(data, 'bundle', 'bundleId')}`;

    case 'removeProduct':
      return `product:${id(data, 'product', 'productId')}`;

    case 'removeModifier':
      return `modifier:${id(data, 'modifier', 'modifierId')}`;

    case 'removeModifierFromSlot':
      return `slotModifier:${id(data, 'slot', 'slotId')}:${id(data, 'modifier', 'modifierId')}`;

    case 'setModifierPriceRanges':
      return `modifierRanges:${id(data, 'variant', 'variantId')}:${id(data, 'slot', 'slotId')}`;

    case 'setModifierWeightPrice':
    case 'setModifierWeightIsExcludedFromVariant':
      return [
        'modifierWeight',
        id(data, 'variant', 'variantId'),
        id(data, 'slot', 'slotId'),
        id(data, 'modifier', 'modifierId'),
        readWeightId(data),
      ].join(':');

    case 'removeWeightFromSlotModifier':
      return [
        'slotModifierWeight',
        id(data, 'slot', 'slotId'),
        id(data, 'modifier', 'modifierId'),
        readWeightId(data),
      ].join(':');

    default:
      return `unknown:${key}`;
  }
};

/** The `__typename` a patch operation node carries, mapped to the input key we use everywhere else. */
const TYPENAME_TO_KEY: Record<string, OperationKey> = {
  SetVariantPriceMenuPatchOperation: 'setVariantPrice',
  SetBundlePriceMenuPatchOperation: 'setBundlePrice',
  SetNamedPriceAmountPatchOperation: 'setNamedPriceAmount',
  SetModifierPriceRangesMenuPatchOperation: 'setModifierPriceRanges',
  SetModifierWeightPriceMenuPatchOperation: 'setModifierWeightPrice',
  SetModifierWeightIsExcludedFromVariantMenuPatchOperation: 'setModifierWeightIsExcludedFromVariant',
  RemoveProductMenuPatchOperation: 'removeProduct',
  RemoveVariantMenuPatchOperation: 'removeVariant',
  RemoveModifierMenuPatchOperation: 'removeModifier',
  RemoveModifierFromSlotPatchOperation: 'removeModifierFromSlot',
  RemoveWeightFromSlotModifierMenuPatchOperation: 'removeWeightFromSlotModifier',
  RemoveBundleMenuPatchOperation: 'removeBundle',
};

/**
 * A patch operation as it arrives from `getPatch` has its fields on the node itself, under a
 * `__typename`, rather than nested under an input key. This presents both shapes the same way so one
 * set of helpers can read either.
 *
 * Returns null when the node is not an operation type we handle.
 */
export const normalizeOperationNode = (node: any): { key: OperationKey; data: any } | null => {
  if (!node || typeof node !== 'object') return null;

  const nestedKey = getOperationKey(node);
  if (nestedKey) return { key: nestedKey, data: node[nestedKey] };

  const key = TYPENAME_TO_KEY[node.__typename];
  return key ? { key, data: node } : null;
};

/** Reads a display name off an entity, preferring the human-facing one and never inventing a label. */
const entityName = (entity: any, idField: string): string | undefined =>
  entity?.name || entity?.internalName || entity?.[idField] || undefined;

/**
 * Entity label readers, shared by everything that describes an operation.
 *
 * These exist because the same operation reaches us in two shapes with genuinely different nesting,
 * and getting the precedence wrong shows a UUID where a name belongs:
 *
 * - built by the operation drawer: the product sits beside the variant, enriched from the menu cache
 * - read from `getPatch`: the product sits *inside* the variant, and the variant has no name at all -
 *   its identity is its selected options ("Medium - Pizza, Hand-Tossed Pizza") or its variantCode
 *
 * Codes are preferred over ids as a last resort: `variant_CLSS001_010BCYO11T_SIZE050M_BASE020H` is at
 * least legible, where a UUID is not.
 */
export const readProductLabel = (data: any): string | undefined =>
  // Sibling first (drawer-built), then nested under the variant (getPatch).
  entityName(data?.product, 'productCode') ||
  entityName(data?.variant?.product, 'productCode') ||
  data?.product?.productId ||
  data?.variant?.product?.productId ||
  undefined;

export const readVariantLabel = (data: any): string | undefined => {
  const variant = data?.variant;
  if (!variant) return undefined;

  if (variant.name || variant.internalName) return variant.name || variant.internalName;

  // A getPatch variant is identified by what was selected, in payload order - "size, crust".
  const selected = Array.isArray(variant.selectedOptions)
    ? variant.selectedOptions
        .map((option: any) => option?.optionValue?.internalName || option?.optionValue?.name)
        .filter(Boolean)
        .join(', ')
    : '';

  return selected || variant.variantCode || variant.variantId || data?.variantId || undefined;
};

export const readSlotLabel = (data: any): string | undefined =>
  entityName(data?.slot, 'slotCode') || data?.slot?.slotId || data?.slotId || undefined;

export const readModifierLabel = (data: any): string | undefined =>
  entityName(data?.modifier, 'modifierCode') || data?.modifier?.modifierId || data?.modifierId || undefined;

export const readWeightLabel = (data: any): string | undefined => {
  const weight = data?.weight ?? data?.modifierWeight;
  return (
    entityName(weight, 'modifierWeightCode') ||
    weight?.modifierWeightId ||
    weight?.weightId ||
    data?.weightId ||
    undefined
  );
};

export const readBundleLabel = (data: any): string | undefined =>
  entityName(data?.bundle, 'bundleCode') || data?.bundle?.bundleId || data?.bundleId || undefined;

export const readNamedPriceLabel = (data: any): string | undefined =>
  entityName(data?.namedPrice, 'namedPriceId') || data?.namedPriceId || undefined;

/**
 * A short, human-readable description of what an operation targets. Self-contained: used where nothing
 * else on screen carries the target, such as a table cell.
 *
 * Falls back through name → internalName → code → id rather than to a placeholder like "Unknown
 * Variant": an id identifies the record, a placeholder identifies nothing.
 */
export const describeOperationTarget = (operation: any): string => {
  const normalized = normalizeOperationNode(operation);
  if (!normalized) return 'Unrecognised operation';

  const { key, data } = normalized;

  const parts: Array<string | undefined> = [];

  switch (key) {
    case 'setNamedPriceAmount':
      parts.push(readNamedPriceLabel(data));
      break;

    case 'setBundlePrice':
    case 'removeBundle':
      parts.push(readBundleLabel(data));
      break;

    case 'removeProduct':
      parts.push(readProductLabel(data));
      break;

    case 'setVariantPrice':
    case 'removeVariant':
      // The product name alone is ambiguous across sizes and crusts, so both are shown.
      parts.push(readProductLabel(data));
      parts.push(readVariantLabel(data));
      break;

    case 'removeModifier':
      parts.push(readModifierLabel(data));
      break;

    case 'removeModifierFromSlot':
      parts.push(readSlotLabel(data));
      parts.push(readModifierLabel(data));
      break;

    case 'setModifierPriceRanges':
      parts.push(readProductLabel(data));
      parts.push(readVariantLabel(data));
      parts.push(readSlotLabel(data));
      break;

    case 'setModifierWeightPrice':
    case 'setModifierWeightIsExcludedFromVariant':
      parts.push(readProductLabel(data));
      parts.push(readVariantLabel(data));
      parts.push(readModifierLabel(data));
      parts.push(readWeightLabel(data));
      break;

    case 'removeWeightFromSlotModifier':
      parts.push(readSlotLabel(data));
      parts.push(readModifierLabel(data));
      parts.push(readWeightLabel(data));
      break;

    default:
      break;
  }

  const description = parts.filter(Boolean).join(' - ');
  return description || 'Unidentified target';
};

/**
 * The amount an operation currently carries, in whole cents, or undefined for operations that hold no
 * single scalar value (removals, and price ranges, which hold several).
 */
export const getOperationAmount = (operation: any): number | undefined => {
  const normalized = normalizeOperationNode(operation);
  if (!normalized) return undefined;

  const { key, data } = normalized;

  switch (key) {
    // Named prices carry a flat amount; everything else priced goes through authoringPrice.
    case 'setNamedPriceAmount':
      return data.amount;
    case 'setVariantPrice':
    case 'setBundlePrice':
    case 'setModifierWeightPrice':
      return data.authoringPrice?.amount;
    default:
      return undefined;
  }
};

/** Whether this operation type has a single editable amount the dialog can offer inline. */
export const isAmountEditable = (operation: any): boolean => {
  const normalized = normalizeOperationNode(operation);
  if (!normalized) return false;

  return (
    normalized.key === 'setNamedPriceAmount' ||
    normalized.key === 'setVariantPrice' ||
    normalized.key === 'setBundlePrice' ||
    normalized.key === 'setModifierWeightPrice'
  );
};

/**
 * The price ranges an operation carries, or undefined when it is not a ranges operation.
 *
 * Distinct from `getOperationAmount` because this is the one type holding several prices rather than
 * one, which is exactly why it has no single amount to show or edit.
 */
export const getOperationPriceRanges = (operation: any): any[] | undefined => {
  const normalized = normalizeOperationNode(operation);
  if (normalized?.key !== 'setModifierPriceRanges') return undefined;

  return Array.isArray(normalized.data.weightPriceRanges) ? normalized.data.weightPriceRanges : undefined;
};

/**
 * Whether this operation holds price ranges the dialog can edit.
 *
 * Type-based, like `isAmountEditable`: an operation whose payload carried no ranges is still a ranges
 * operation, and setting ranges on it is a legitimate edit.
 */
export const arePriceRangesEditable = (operation: any): boolean =>
  normalizeOperationNode(operation)?.key === 'setModifierPriceRanges';

/**
 * True when an operation carries a single amount by nature but the payload did not include it.
 *
 * Real case: `/api/patches/:id` omits `authoringPrice` from every `SetBundlePriceMenuPatchOperation`,
 * even though the schema declares it non-null. Distinguishing this from an operation that has no single
 * amount at all (price ranges hold several) is the difference between telling the user the current price
 * is unavailable and showing them a blank field to edit blind.
 */
export const isAmountMissing = (operation: any): boolean =>
  isAmountEditable(operation) && getOperationAmount(operation) === undefined;
